import type { VariableDefinition } from "@babackapp/variables-registry";
import type { PublicShopDto } from "@babackapp/private-api-client/types";
import { useEffect, useMemo } from "react";
import { type FieldPath, useFormContext } from "react-hook-form";
import type { BlockEditorFormValues } from "../types";
import { BlockSection } from "./block-section";
import { CodeEditorView } from "./code-editor-view";
import { WebMainContainer } from "./layout/web-main-container";
import { EmailCardContainer } from "./layout";
import { ShopProvider } from "./shop-provider";
import { type EditorMode, EditorContext } from "./editor";
import defaultEmailsTheme from "../constants/default-emails-theme";

type WebEditorProps = {
  shop: PublicShopDto;
  currentLocale: string;
  onLocaleChange?: (locale: string) => void;
  viewport?: "desktop" | "mobile" | "code";
  variables: VariableDefinition[];
  editorMode?: EditorMode;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
};

export default function WebEditor({
  shop,
  currentLocale,
  onLocaleChange,
  variables,
  viewport = "desktop",
  editorMode = "default",
  hiddenBlocks,
  onToggleBlockVisibility,
  customizedBlockIds,
}: WebEditorProps) {
  const form = useFormContext<BlockEditorFormValues>();
  const values = form.watch();
  const assets = values.assets;

  useEffect(() => {
    if (assets) return;
    form.setValue(
      "assets" as FieldPath<BlockEditorFormValues>,
      { uploads: {} },
      {
        shouldDirty: false,
        shouldTouch: false,
      },
    );
  }, [assets, form]);

  const beforeBlocks = values.content.beforeBlocks || [];
  const insideBlocks = values.content.insideBlocks || [];
  const afterBlocks = values.content.afterBlocks || [];

  const theme = shop.trackingPreferences?.emailsTheme || defaultEmailsTheme;
  const backgroundColor = theme?.backgroundColor || "#fcfcfc";
  const cardColor = theme?.cardColor || "#ffffff";
  const cardTextColor = theme?.cardTextColor;

  const defaultLocale =
    shop?.trackingPreferences?.notificationsDefaultLocale || "en";

  const editorContextValue = useMemo(
    () => ({
      currentLocale,
      onLocaleChange,
      variables,
      defaultLocale,
      theme,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    }),
    [
      currentLocale,
      onLocaleChange,
      variables,
      defaultLocale,
      theme,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    ],
  );

  return (
    <ShopProvider shop={shop}>
      <EditorContext.Provider value={editorContextValue}>
        {viewport === "code" ? (
          <CodeEditorView />
        ) : (
          <WebMainContainer
            viewport={viewport}
            backgroundColor={backgroundColor}
            isEditor={true}
            editorStyle={
              {
                position: "relative",
                "--primary":
                  shop.preferences?.customisation["primary_400"] ??
                  shop.preferences?.customisation["primary-400"] ??
                  "#000000",
              } as React.CSSProperties
            }
          >
            <BlockSection
              section="before"
              fields={beforeBlocks}
              platform="web"
            />
            <EmailCardContainer
              theme={theme}
              cardColor={cardColor}
              cardTextColor={cardTextColor}
              isEditor={true}
            >
              <BlockSection
                section="inside"
                fields={insideBlocks}
                platform="web"
              />
            </EmailCardContainer>
            <BlockSection section="after" fields={afterBlocks} platform="web" />
          </WebMainContainer>
        )}
      </EditorContext.Provider>
    </ShopProvider>
  );
}
